import type { AdminInspectorDetailDto } from '@carcheck/shared';
import Typography from '@mui/material/Typography';
import { useEffect, useMemo, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { useAdminCardsList } from '../../../api/admin-cards';
import { useCursorPagination } from '../../../hooks/useCursorPagination';
import { CardsGrid } from '../../cards';

/**
 * Inspector-detail Cards tab (AUT-126) — replaces `CardsTabPlaceholder`.
 *
 * Fetches `GET /admin/cards?inspectorId=<id>` (the SAME cross-inspector list
 * endpoint the standalone `/cards` page hits, scoped to one inspector) and
 * renders the SAME shared `<CardsGrid>` — so the card-row renderer + the
 * force-unbind dialog/hook are ONE source, never duplicated.
 *
 * The query is gated to the active tab: the drawer mounts this component
 * only via `{tab === 'cards' ? <CardsTab .../> : null}`, so the fetch never
 * fires until the operator opens the Cards tab (and React Query unmounts +
 * cancels it when they switch away). Pagination is tab-local (a fresh
 * `useCursorPagination`) — the inspector is fixed, so there is no URL filter
 * state to thread here.
 *
 * Force-unbind inside the tab runs the same `process_payouts` step-up + the
 * same `['admin','cards']` invalidation as the standalone page (so unbinding
 * from the drawer re-syncs the standalone list too).
 */
export const CardsTab = ({ inspector }: { inspector: AdminInspectorDetailDto }): ReactElement => {
  const { t } = useTranslation();
  const pager = useCursorPagination();

  const query = useAdminCardsList({
    inspectorId: inspector.id,
    cursor: pager.cursor,
    limit: pager.pageSize,
  });

  const items = useMemo(() => query.data?.items ?? [], [query.data]);
  const nextCursor = query.data?.nextCursor ?? null;

  useEffect(() => {
    if (query.isSuccess) pager.onResult(nextCursor);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query.isSuccess, query.dataUpdatedAt, nextCursor]);

  return (
    <CardsGrid
      rows={items}
      rowCount={pager.rowCount(items.length)}
      loading={query.isLoading || query.isFetching}
      error={query.error}
      paginationModel={pager.paginationModel}
      onPaginationModelChange={pager.onPaginationModelChange}
      emptyState={
        <Typography variant="body2" color="text.secondary">
          {t('cards.emptyForInspector')}
        </Typography>
      }
    />
  );
};
